import { useDateNow, useReduxState } from 'hooks';
import { Link } from 'components/common';
import { Icon } from 'components/common/icons';
import { Page } from 'components/page';
import { ErrorImage, ErrorPage404 } from '.';

import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  appError?: boolean;
  description?: string;
  icon?: IconProp;
  statusCode?: number;
  title?: string;
}

const ErrorPage = ({
  appError = false,
  description,
  icon,
  statusCode,
  title
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme, user } = useReduxState();

  return (
    <div className="error">
      <Page
        appError={appError}
        showUser={user && !user.locked}
        subheader={user && user.roles.includes('user') && !user.locked}
      >
        <div className="content">
          {icon && title ? (
            <>
              <div className="icon">
                <Icon icon={icon} />
              </div>
              <div className="title">{title}</div>
              {description ? <p>{description}</p> : null}
              <div className="link">
                <Link href="/" text={messages.errorGoToMainPage} />
              </div>
            </>
          ) : statusCode === 403 && user && user.locked ? (
            <>
              <Icon
                icon="lock"
                style={{ fontSize: '2.4rem', marginBottom: 5 }}
              />
              <div className="title">{messages.usersAccountLocked}</div>
              <p>{messages.authAccountLocked}</p>
              <div className="link">
                <Link href="/login" text={messages.authBackToLogin} />
              </div>
            </>
          ) : statusCode === 403 &&
            user &&
            user.expirationDate &&
            Date.parse(user.expirationDate as string) < dateNow ? (
            <>
              <Icon
                icon={['far', 'calendar-times']}
                style={{ fontSize: '2.4rem', marginBottom: 5 }}
              />
              <div className="title">{messages.usersAccountExpired}</div>
              <p>{messages.authAccountExpired}</p>
              <div className="link">
                <Link href="/login" text={messages.authBackToLogin} />
              </div>
            </>
          ) : statusCode === 403 ? (
            <>
              <div className="icon">
                <Icon icon="lock" />
              </div>
              <div className="title">{messages.errorForbidden}</div>
              <p>{messages.errorPermission}</p>
              <div className="link">
                <Link href="/" text={messages.errorGoToMainPage} />
              </div>
            </>
          ) : statusCode === 404 ? (
            <ErrorPage404 />
          ) : (
            <>
              <ErrorImage />
              <div className="title">{messages.errorInternal}</div>
              <p>{messages.errorInternalComment}</p>
              <p>
                {`${messages.errorInternalInfo} `}
                <Link
                  external={true}
                  href="mailto:help@vuolearning.fi"
                  text="help@vuolearning.fi"
                />
              </p>
              {!appError ? (
                <div className="link">
                  <Link href="/" text={messages.errorGoToMainPage} />
                </div>
              ) : null}
            </>
          )}
        </div>
      </Page>

      <style jsx>{`
        .error {
          height: 100%;
        }
        .error :global(.page-content) {
          align-items: center;
          display: flex;
        }
        .content {
          font-size: 1.6rem;
          font-weight: 600;
          margin: 0 auto 50px;
          max-width: 320px;
          text-align: center;
        }
        .icon {
          background-color: ${theme.lightGreyDark};
          border-radius: 50px;
          color: ${theme.textColorLight};
          display: inline-block;
          font-size: 2rem;
          height: 50px;
          padding: 12px 0;
          width: 50px;
          margin-bottom: 5px;
        }
        .title {
          color: ${theme.textColor};
          font-size: 2rem;
          font-weight: 700;
          margin: 5px 0 10px;
        }
        .content p {
          color: ${theme.textColorLighter};
          margin: 10px 0;
        }
        .link {
          margin-top: 25px;
        }
      `}</style>
    </div>
  );
};

export default ErrorPage;
